import {
  AfterViewInit,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef,
  ViewChildren,
  QueryList,
} from "@angular/core";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { AutopilotRepository } from "../../../domain/autopilot-repository";
import { UserService } from "../../../domain/user-service";
import { Logger } from "../../../infrastructure/logger";
import {
  EStreamSourceType,
  LiveFeedEncryptionTypeEnum,
  LiveFeedInfo,
  LiveFeedStreamingPlatform,
  StreamSourceSettings,
  StreamingPlatform,
} from "../../../domain/live-feed";
import { LiveFeedRepository } from "../../../domain/live-feed-repository";
import { UI } from "../../../services/ui.service";
import randomBytes from "randombytes";
import { PopOverComponent } from "../../pop-over/pop-over.component";
import { UntypedFormGroup } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import { TagRepository } from "../../../domain/tag-repository";
import { Tag } from "../../../domain/tag";
import { Socket } from "../../../infrastructure/socket.service";
import { WizardStepComponent } from "../../common/wizard/wizard-step.component";
import { IndicateStatus } from "../../../domain/enums";

import { from as observableFrom, fromEvent, Observable, Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";

interface StreamIdChangedEvent {
  platformId: number;
  streamName: string;
}

@Component({
  selector: "icn-live-feed-create-edit",
  templateUrl: "./live-feed-create-edit.component.html",
  styleUrls: ["./live-feed-create-edit.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LiveFeedCreateEditComponent
  implements AfterViewInit, OnInit, OnDestroy
{
  private static readonly IP_RANGE_REGEX =
    /^((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\/(3[0-2]|[12]?\d))?$/;

  constructor(
    private logger: Logger,
    private route: ActivatedRoute,
    private liveFeedRepository: LiveFeedRepository,
    private userService: UserService,
    private rulesRepository: AutopilotRepository,
    private changeDetectorRef: ChangeDetectorRef,
    private ui: UI,
    private toastr: ToastrService,
    private tagRepository: TagRepository,
    private socketService: Socket,
    private _router: Router,
  ) {}

  @ViewChild("encryptionPopover") encryptionPopover: PopOverComponent;
  @ViewChild("copiedPopover") copiedPopover: PopOverComponent;
  @ViewChild("liveFeedForm") liveFeedForm;
  @ViewChildren("tenantUniqueIdElement")
  tenantUniqueIdElement: QueryList<ElementRef>;

  encryptionPopoverVisibleSubscription;
  encryptionPopoverVisible: boolean;

  streamIdChanged = new EventEmitter<StreamIdChangedEvent>();

  channelAvailabilityZones = [];
  streamIdUniqueStatus = new Subject<IndicateStatus>();
  feedIdUniqueStatus = new Subject<IndicateStatus>();
  originalStreamNamesByPlatformId = new Map<number, string>();
  originalFeedId: string;

  allStreamingSourceTypes: { id: number; displayName: string }[] = [];
  liveFeed: LiveFeedInfo;
  edit = false;
  isStreamAvailable;
  isFeedIdAvailable: boolean;
  copiedInput: string;

  whitelistIpInput = "";
  whitelistIpError = "";

  allStreamingPlatforms: StreamingPlatform[];

  selectedPlatformIndex = 0;

  allTags: Tag[];

  liveFeedPreview;

  frameFormats = [
    { displayName: "Progressive", value: 1 },
    { displayName: "Top Field First", value: 2 },
    { displayName: "Bottom Field First", value: 3 },
    { displayName: "Unknown", value: 0 },
  ];

  getHeader() {
    return this.edit ? "EDIT LIVE FEED" : "CREATE LIVE FEED";
  }

  async ngOnInit() {
    this.channelAvailabilityZones =
      this.userService.getCurrentUser().channelAvailabilityZones;
    this.subscribeUniqueIdValidator();
    this.allStreamingPlatforms =
      await this.liveFeedRepository.getAllStreamingPlatforms();

    this.allTags = this.tagRepository.getGeneralTags();

    this.initStreamingSourceTypes();

    this.subscribeToRoute();
  }

  ngOnDestroy() {
    if (this.encryptionPopoverVisibleSubscription) {
      this.encryptionPopoverVisibleSubscription.unsubscribe();
    }
    this.clearPreview();
  }

  showPreview() {
    const platform = this.getActivePlatform();
    if (!platform || !this.liveFeed?.id) {
      return;
    }
    this.socketService
      .createLiveFeedPreview(
        this.liveFeed.id,
        platform.availabilityZoneId,
        platform.id,
      )
      .then((liveFeedPreview) => {
        this.liveFeedPreview = liveFeedPreview;
        this.changeDetectorRef.markForCheck();
      });
  }

  private clearPreview() {
    if (!this.liveFeedPreview) {
      return;
    }
    const previewId = this.liveFeedPreview.id;
    this.liveFeedPreview = null;
    this.socketService.deactivateLiveFeedPreview(previewId).catch((err) => {
      this.logger.warn("Failed to deactivate live feed preview: " + err);
    });
    this.changeDetectorRef.markForCheck();
  }

  hasStreamingPlatforms(liveFeed: LiveFeedInfo): boolean {
    return Object.keys(liveFeed.streamingPlatforms).length > 0;
  }

  getTitle() {
    if (this.liveFeed.id === "") {
      return `Create Your Live Feed`;
    } else {
      return `Edit Your Live Feed`;
    }
  }

  onApply() {
    this.saveLiveFeed().catch((err) => {
      console.log(err);
    });
  }

  saveLiveFeed() {
    if (!this.canSaveLiveFeed()) {
      this.markFormGroupTouched(this.liveFeedForm.form);
      return;
    }

    const promise = !!this.liveFeed.id
      ? (x) => this.liveFeedRepository.updateLiveFeed(x)
      : (x) => this.liveFeedRepository.createLiveFeed(x);
    return promise(this.liveFeed)
      .then(() => {
        this.streamIdUniqueStatus.next(IndicateStatus.Success);
        this.feedIdUniqueStatus.next(IndicateStatus.Success);
        this.isStreamAvailable = true;
        this.isFeedIdAvailable = true;
        this.refreshOriginalStreamNames();
        this.originalFeedId = this.liveFeed.tenantUniqueId;
        this.toastr.info("Saved");
        this.changeDetectorRef.markForCheck();
        return this._router.navigate([
          "/scheduler/live/edit",
          this.liveFeed.id,
        ]);
      })
      .catch((err) => {
        if (err.status === 400) {
          this.ui.notify(this.ui.EVENTS.ShowAlert, {
            title: "Live Feed",
            message: "An error occurred",
          });
        }
        throw err;
      });
  }

  onNextHandler(event) {
    const previousStep: WizardStepComponent = event.previousStep;
  }

  onBackHandler(event) {
    const previousStep: WizardStepComponent = event.previousStep;
  }

  private initStreamingSourceTypes() {
    const visibleStreamTypes = [
      EStreamSourceType.ZIXI_PULL,
      EStreamSourceType.ZIXI_PUSH,
      EStreamSourceType.RTMP_PUSH,
      EStreamSourceType.RTMP_PULL,
      EStreamSourceType.SRT_PUSH,
      EStreamSourceType.SRT_PULL,
    ];
    this.allStreamingSourceTypes = Object.keys(EStreamSourceType)
      .filter((prop: any) => isNaN(prop) === false)
      .map((enumIntValue) => {
        return {
          id: parseInt(enumIntValue),
          displayName: EStreamSourceType[enumIntValue]
            .toString()
            .replace("_", " "),
        };
      })
      .filter((streamType) => visibleStreamTypes.includes(streamType.id));

    console.log("types", visibleStreamTypes, this.allStreamingSourceTypes);
  }

  onStreamIdChanged(newStreamName: string) {
    const platform = this.getActivePlatform();
    if (!platform) {
      return;
    }
    platform.streamSourceSettings.streamName = newStreamName;
    this.streamIdUniqueStatus.next(IndicateStatus.Loading);
    this.streamIdChanged.emit({
      platformId: platform.id,
      streamName: newStreamName,
    });
    this.changeDetectorRef.markForCheck();
    this.copiedInput = "";
  }

  onFeedNameChanged(feedName) {
    this.liveFeed.name = feedName;
    this.copiedInput = "";
  }

  toggleEnableEncryption(event) {
    const settings = this.getActiveSettings();
    if (!settings) {
      return;
    }
    if (settings.encryptionType !== 0) {
      settings.encryptionType = LiveFeedEncryptionTypeEnum.None;
      settings.encryptionKey = "";
      this.encryptionPopover.hide();
    } else {
      settings.encryptionType = LiveFeedEncryptionTypeEnum.AES128;
      this.encryptionPopover.show(event);
    }
  }

  subscribeUniqueIdValidator() {
    observableFrom(this.streamIdChanged)
      .pipe(debounceTime(500))
      .subscribe((event: StreamIdChangedEvent) => {
        const originalName = this.originalStreamNamesByPlatformId.get(
          event.platformId,
        );
        if (!event.streamName || event.streamName === originalName) {
          this.streamIdUniqueStatus.next(IndicateStatus.Success);
          return;
        }

        const liveFeed = this.liveFeed;
        this.liveFeedRepository
          .isStreamIdAvailable(event.streamName)
          .then((isAvailable) => {
            if (this.liveFeed !== liveFeed) {
              return;
            }
            this.isStreamAvailable = isAvailable;
            this.streamIdUniqueStatus.next(
              isAvailable ? IndicateStatus.Success : IndicateStatus.Failed,
            );
            this.changeDetectorRef.markForCheck();
          });
      });
  }
  platformsEqualsFunc = (a, b) => a && b && a.id === b.id;

  generateEncryptionKey() {
    const settings = this.getActiveSettings();
    if (!settings) {
      return;
    }
    const keyLengthInBits = this.getEncryptionLengthInBits();
    const randombytes = new Uint8Array(randomBytes(keyLengthInBits / 8));
    settings.encryptionKey = Array.from(randombytes)
      .map((b) => {
        const str = b.toString(16);
        return str.length === 1 ? "0" + str : str;
      })
      .join("");
  }

  getEncryptionLengthInBits() {
    const settings = this.getActiveSettings();
    if (!settings) {
      return 0;
    }
    switch (settings.encryptionType) {
      case LiveFeedEncryptionTypeEnum.AES128:
        return 128;
      case LiveFeedEncryptionTypeEnum.AES192:
        return 192;
      case LiveFeedEncryptionTypeEnum.AES256:
        return 256;
    }
  }

  copyToClipboard(value, event, copiedInput) {
    if (!value) {
      return;
    }

    const temp = document.createElement("input");
    document.body.appendChild(temp);
    temp.setAttribute("id", "temp_id");
    temp.value = value;
    temp.select();
    document.execCommand("copy");
    document.body.removeChild(temp);

    this.copiedPopover.show(event);
    this.copiedInput = copiedInput;
  }

  private subscribeToRoute() {
    this.route.params.subscribe((params: Params) => {
      this.isStreamAvailable = true;
      this.isFeedIdAvailable = true;
      this.liveFeedPreview = null;
      if (!!params["id"]) {
        this.liveFeedRepository
          .getLiveFeedInfo(params["id"])
          .then((liveFeedInfo) => {
            this.liveFeed = liveFeedInfo;
            this.selectedPlatformIndex = 0;
            this.refreshOriginalStreamNames();
            this.originalFeedId = liveFeedInfo.tenantUniqueId;
            this.streamIdUniqueStatus.next(IndicateStatus.Success);
            this.feedIdUniqueStatus.next(IndicateStatus.Success);
            this.edit = true;
            this.changeDetectorRef.markForCheck();
          })
          .catch((err) => {
            this.logger.warn("Error getting channel info. Error: " + err);
          });
      } else {
        this.originalStreamNamesByPlatformId.clear();
        this.originalFeedId = "";
        this.edit = false;

        const metadata = this.userService.getCurrentUser().metadata.map((x) => {
          return { name: x.name, value: "" };
        });

        const streamingPlatforms = this.allStreamingPlatforms.map(
          (p) =>
            new LiveFeedStreamingPlatform(
              p.id,
              p.privateIp,
              p.publicIp,
              p.availabilityZoneId,
              p.availabilityZoneName,
              this.createDefaultStreamSourceSettings(),
            ),
        );

        this.liveFeed = new LiveFeedInfo(
          "",
          "",
          "",
          "",
          metadata,
          [],
          0,
          3000,
          10000,
          streamingPlatforms,
        );
        this.selectedPlatformIndex = 0;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  private createDefaultStreamSourceSettings(): StreamSourceSettings {
    return new StreamSourceSettings(
      EStreamSourceType.ZIXI_PUSH,
      LiveFeedEncryptionTypeEnum.None,
      "",
      "",
      "",
      "",
      2088,
      0,
      "",
      "",
      "",
      6000,
      "",
      [],
    );
  }

  private refreshOriginalStreamNames() {
    this.originalStreamNamesByPlatformId.clear();
    if (!this.liveFeed) {
      return;
    }
    for (const p of this.liveFeed.streamingPlatforms) {
      this.originalStreamNamesByPlatformId.set(
        p.id,
        p.streamSourceSettings.streamName,
      );
    }
  }

  getActivePlatform(): LiveFeedStreamingPlatform | null {
    if (
      !this.liveFeed ||
      !this.liveFeed.streamingPlatforms ||
      this.liveFeed.streamingPlatforms.length === 0
    ) {
      return null;
    }
    const i = Math.min(
      this.selectedPlatformIndex,
      this.liveFeed.streamingPlatforms.length - 1,
    );
    return this.liveFeed.streamingPlatforms[i];
  }

  getActiveSettings(): StreamSourceSettings | null {
    const platform = this.getActivePlatform();
    return platform ? platform.streamSourceSettings : null;
  }

  selectPlatform(index: number) {
    this.clearPreview();
    this.clearWhitelistIpInput();
    this.selectedPlatformIndex = index;
    this.streamIdUniqueStatus.next(IndicateStatus.Success);
    this.changeDetectorRef.markForCheck();
  }

  ngAfterViewInit() {
    this.tenantUniqueIdElement.changes.subscribe((q) => {
      const tenantUniqueIdElement = q.first;
      fromEvent(tenantUniqueIdElement.nativeElement, "keyup")
        .pipe(debounceTime(500))
        .subscribe((event: any) => {
          const newFeedId = event.target.value;
          console.log("tenantUniqueIdElementChanged->", newFeedId);
          if (!newFeedId || newFeedId === this.originalFeedId) {
            this.feedIdUniqueStatus.next(IndicateStatus.Success);
            return;
          }
          this.feedIdUniqueStatus.next(IndicateStatus.Loading);
          const liveFeed = this.liveFeed;
          this.liveFeedRepository
            .isFeedIdAvailable(newFeedId)
            .then((isAvailable) => {
              if (this.liveFeed !== liveFeed) {
                return;
              }
              this.isFeedIdAvailable = isAvailable;
              this.feedIdUniqueStatus.next(
                isAvailable ? IndicateStatus.Success : IndicateStatus.Failed,
              );
              this.changeDetectorRef.markForCheck();
            });
        });
    });

    this.encryptionPopoverVisibleSubscription =
      this.encryptionPopover.visible$.subscribe((visible) => {
        this.encryptionPopoverVisible = visible;
      });
  }

  getAzDisplayName(id) {
    const az = this.channelAvailabilityZones.find((x) => x.id === id);
    return az.displayName;
  }

  getAllTagNames() {
    return this.allTags
      .map((x) => x.name)
      .filter((v, i, a) => a.indexOf(v) === i);
  }

  getFeedTagNames() {
    return this.getFeedTags().map((x) => x.name);
  }

  getFeedTags() {
    return this.liveFeed.tagIds.map((x) =>
      this.allTags.find((y) => y.id === x),
    );
  }

  deleteTag(tag) {
    const index = this.liveFeed.tagIds.findIndex((x) => x === tag.id);
    if (index > -1) {
      this.liveFeed.tagIds.splice(index, 1);
    }
  }

  createNewTag(tag) {
    if (tag.trim() === "") {
      return;
    }
    this.tagRepository
      .CreateGeneralTag(tag)
      .then((response) => {
        if (
          this.allTags.findIndex((tagItem) => tagItem.id === response.id) === -1
        ) {
          this.allTags.push(response);
        }
        this.addTag(tag);
        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
      })
      .catch((err) => {
        console.error(err);
      });
  }

  addTag(tag) {
    const id = this.allTags.find((x) => x.name === tag.trim()).id;
    if (this.liveFeed.tagIds.findIndex((x) => x === id) === -1) {
      this.liveFeed.tagIds.push(id);
    }
    this.changeDetectorRef.markForCheck();
  }

  private canSaveLiveFeed() {
    const platforms = this.liveFeed.streamingPlatforms;
    const allPlatformsValid = platforms.every(
      (p) =>
        p.streamSourceSettings.encryptionType ===
          LiveFeedEncryptionTypeEnum.None ||
        !!p.streamSourceSettings.encryptionKey,
    );
    return (
      this.liveFeedForm.form.valid &&
      this.isStreamAvailable &&
      this.isFeedIdAvailable &&
      allPlatformsValid &&
      platforms.length > 0
    );
  }

  private markFormGroupTouched(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls)
      .map((x) => formGroup.controls[x])
      .forEach((control) => {
        control.markAsTouched();
      });
  }

  onSourceTypeChanged() {
    this.clearWhitelistIpInput();
  }

  showWhitelistIpRangesField(sourceType: EStreamSourceType): boolean {
    return sourceType === EStreamSourceType.SRT_PUSH;
  }

  addWhitelistIpRange(settings: StreamSourceSettings) {
    const range = this.whitelistIpInput.trim();
    if (!range) {
      return;
    }
    if (LiveFeedCreateEditComponent.IP_RANGE_REGEX.test(range) === false) {
      this.whitelistIpError =
        "Invalid IP range. Expected format: 213.57.118.227/24";
      return;
    }
    if (!settings.whitelistIpRanges) {
      settings.whitelistIpRanges = [];
    }
    if (settings.whitelistIpRanges.includes(range) === false) {
      settings.whitelistIpRanges.push(range);
    }
    this.clearWhitelistIpInput();
  }

  removeWhitelistIpRange(settings: StreamSourceSettings, range: string) {
    settings.whitelistIpRanges = settings.whitelistIpRanges.filter(
      (r) => r !== range,
    );
  }

  onWhitelistIpInputChange(value: string) {
    this.whitelistIpInput = value;
    this.whitelistIpError = "";
  }

  private clearWhitelistIpInput() {
    this.whitelistIpInput = "";
    this.whitelistIpError = "";
  }

  showUrlField(sourceType: EStreamSourceType): boolean {
    return (
      sourceType === EStreamSourceType.DASH_PULL ||
      sourceType === EStreamSourceType.RTSP_PULL ||
      sourceType === EStreamSourceType.RTMP_PULL ||
      sourceType === EStreamSourceType.HLS_PULL
    );
  }

  showSpecialOptionsField(sourceType: EStreamSourceType): boolean {
    return (
      sourceType === EStreamSourceType.SRT_PULL ||
      sourceType === EStreamSourceType.SRT_PUSH
    );
  }

  showStreamNameField(sourceType: EStreamSourceType): boolean {
    return (
      sourceType === EStreamSourceType.ZIXI_PULL ||
      sourceType === EStreamSourceType.ZIXI_PUSH ||
      sourceType === EStreamSourceType.RTMP_PUSH
    );
  }

  showLivePrefix(sourceType: EStreamSourceType): boolean {
    return (
      sourceType === EStreamSourceType.ZIXI_PULL ||
      sourceType === EStreamSourceType.ZIXI_PUSH ||
      sourceType === EStreamSourceType.RTMP_PUSH
    );
  }

  showZixiRemoteNameField(sourceType: EStreamSourceType): boolean {
    return sourceType === EStreamSourceType.ZIXI_PULL;
  }

  showRemoteAddressField(sourceType: EStreamSourceType): boolean {
    return (
      sourceType === EStreamSourceType.ZIXI_PULL ||
      sourceType === EStreamSourceType.SRT_PULL ||
      sourceType === EStreamSourceType.TCP_PULL
    );
  }

  showRemotePortField(sourceType: EStreamSourceType): boolean {
    return this.showRemoteAddressField(sourceType);
  }

  showPasswordField(sourceType: EStreamSourceType): boolean {
    return (
      sourceType === EStreamSourceType.ZIXI_PULL ||
      sourceType === EStreamSourceType.ZIXI_PUSH ||
      sourceType === EStreamSourceType.RTMP_PULL ||
      sourceType === EStreamSourceType.SRT_PUSH ||
      sourceType === EStreamSourceType.SRT_PULL
    );
  }

  showUsernameField(sourceType: EStreamSourceType): boolean {
    return sourceType === EStreamSourceType.RTMP_PULL;
  }

  showEncryptionField(sourceType: EStreamSourceType): boolean {
    return (
      sourceType === EStreamSourceType.ZIXI_PULL ||
      sourceType === EStreamSourceType.ZIXI_PUSH /*||
      sourceType === EStreamSourceType.SRT_PUSH ||
      sourceType === EStreamSourceType.SRT_PULL*/
    );
  }

  showLocalPortField(sourceType: EStreamSourceType): boolean {
    return (
      sourceType === EStreamSourceType.TCP_PUSH ||
      sourceType === EStreamSourceType.SRT_PUSH
    );
  }

  showAddressPreview(sourceType: EStreamSourceType): boolean {
    return (
      sourceType === EStreamSourceType.ZIXI_PUSH ||
      sourceType === EStreamSourceType.RTMP_PUSH ||
      sourceType === EStreamSourceType.TCP_PUSH ||
      sourceType === EStreamSourceType.SRT_PUSH
    );
  }

  generateAddressPreview(
    publicIp: string,
    settings: StreamSourceSettings,
  ): string {
    if (settings.sourceType === EStreamSourceType.RTMP_PUSH) {
      return `rtmp://${publicIp}/live/live_${settings.streamName}`;
    } else {
      return publicIp;
    }
  }

  onPlatformsDropdownChange(newPlatforms: StreamingPlatform[]) {
    let lastAddedIndex = -1;
    const merged = newPlatforms.map((p, i) => {
      if (p instanceof LiveFeedStreamingPlatform) {
        return p;
      }
      lastAddedIndex = i;
      return new LiveFeedStreamingPlatform(
        p.id,
        p.privateIp,
        p.publicIp,
        p.availabilityZoneId,
        p.availabilityZoneName,
        this.createDefaultStreamSourceSettings(),
      );
    });
    this.liveFeed.streamingPlatforms = merged;

    if (lastAddedIndex !== -1) {
      this.selectedPlatformIndex = lastAddedIndex;
      this.streamIdUniqueStatus.next(IndicateStatus.Success);
    } else if (this.selectedPlatformIndex >= merged.length) {
      this.selectedPlatformIndex = Math.max(0, merged.length - 1);
    }
    this.changeDetectorRef.markForCheck();
  }
}
